// eslint-disable-next-line import/no-cycle
import { CoreError } from '@goodt-wcore/errors';

/** @type {AuthManager} */
let authManager = null;
const authManagerEnforcer = Symbol('authManagerEnforcer');

/**
 * @typedef {object} AdapterInfo
 * @property {string} name      name
 * @property {Record<string, any>} config default config
 */
export default class AuthManager {
    _adapters = [];
    /** @type {import('./AuthManager').AuthManagerAuthErrorHandler[]>} */
    _authErrorHandlers = [];
    /** @type {import('./AuthManager').AuthManagerLoginHandler[]>} */
    _loginHandlers = [];
    /** @type {import('./AuthManager').AuthManagerLogoutHandler[]>} */
    _logoutHandlers = [];

    setAdapters(adapters) {
        this._adapters = adapters;
    }

    /**
     * Constructor
     *
     * @param {symbol} enforcer  singleton enforcer
     */
    constructor(enforcer) {
        if (enforcer !== authManagerEnforcer) {
            throw new Error(`Instantiation failed: use AuthManager.instance`);
        }
        /**
         * @property {Adapter}
         */
        this._adapter = null;
    }

    /**
     * @return {AuthManager}
     */
    static get instance() {
        if (authManager == null) {
            authManager = new AuthManager(authManagerEnforcer);
        }
        return authManager;
    }

    /**
     * @return {AuthManager}
     */
    static create() {
        return new AuthManager(authManagerEnforcer);
    }

    /**
     * Initializes manager with the specified adapter
     *
     * @param {string} adapterName  adapter name
     * @param {Record<string, any>} config       adapter config
     * @return {Promise}
     */
    async init(adapterName, config = {}) {
        this._adapter = this._createAdapter(adapterName, config);
        return this._adapter.init();
    }

    /**
     * Destroys adapter
     */
    destroy() {
        return new Promise((resolve) => {
            if (this.adapter) {
                this.adapter.destroy().finally(() => {
                    this._adapter = null;
                    resolve();
                });
            } else {
                resolve();
            }
        });
    }

    /**
     * Invokes login handlers
     * @return {Promise}
     */
    login() {
        return Promise.all(this._loginHandlers.map((handler) => handler()));
    }

    /**
     * Invokes login handlers
     * @return {Promise}
     */
    logout() {
        return Promise.all(this._logoutHandlers.map((handler) => handler()));
    }

    /**
     * Invokes auth error handlers
     * @param {Error} [error]
     */
    authError(error) {
        this._authErrorHandlers.forEach((handler) => {
            handler(error);
        });
    }

    /**
     * Adds auth error observer
     * @param {import('./AuthManager').AuthManagerAuthErrorHandler} handler
     * @return {Function}
     */
    onAuthError(handler) {
        this._authErrorHandlers.push(handler);
        return () => {
            this._authErrorHandlers = this._authErrorHandlers.filter((fn) => fn !== handler);
        };
    }

    /**
     * Adds a new login observer
     * @param {import('./AuthManager').AuthManagerLoginHandler} handler
     * @return {Function}
     */
    onLogin(handler) {
        this._loginHandlers.push(handler);
        return () => {
            this._loginHandlers = this._loginHandlers.filter((fn) => fn !== handler);
        };
    }

    /**
     * Adds a new logout observer
     * @param {import('./AuthManager').AuthManagerLoginHandler} handler
     * @return {Function}
     */
    onLogout(handler) {
        this._logoutHandlers.push(handler);
        return () => {
            this._logoutHandlers = this._logoutHandlers.filter((fn) => fn !== handler);
        };
    }

    /**
     * Current used adapter
     *
     * @return {Adapter} adapter
     */
    get adapter() {
        return this._adapter;
    }

    /**
     * Available adapters list
     *
     * @return {object.<string, Adapter>}
     */
    // eslint-disable-next-line class-methods-use-this
    get adaptersList() {
        return this._adapters;
    }

    /**
     * Available adapters info
     *
     * @return {AdapterInfo[]}
     */
    // eslint-disable-next-line class-methods-use-this
    get adaptersInfo() {
        const adapters = Object.entries(this._adapters).map(([name, Ctor]) => ({
            name,
            config: new Ctor().configDefault
        }));

        return adapters;
    }

    /**
     * @private
     * @param {string} adapterName
     * @param {Record<string, any>} config
     * @return
     */
    // eslint-disable-next-line class-methods-use-this
    _createAdapter(adapterName, config) {
        const AdapterClass = this._adapters[adapterName];
        if (AdapterClass == null) {
            throw new CoreError(`AuthManager adapter not found: ${adapterName}`);
        }
        try {
            return new AdapterClass(config);
        } catch (reason) {
            const error = new CoreError(`AuthManager adapter "${adapterName}" create failed`, { reason });
            if (Error.captureStackTrace) {
                Error.captureStackTrace(error, this._createAdapter);
            }
            throw error;
        }
    }
}
